/**
 * Responsibility: Implement game store helpers behavior for the ui/stores surface.
 * Scope: Owns ui/stores concerns only; cross-surface orchestration is excluded.
 */
import type { GameState, AttributeKey, Trait, Ability, Skill } from "@anyquest/shared-core"
import { ActionCategory } from "@anyquest/shared-core"
import { parseLocKey, skillLevelMultiplier } from '../../engine/state'

const ENTROPY_KEYWORDS = ['volatile', 'mutable', 'entropic', 'shifting', 'mercurial', 'flux', 'unstable', 'ephemeral', 'protean', 'shape-shift']
const ATTRIBUTE_KEYS: AttributeKey[] = ['STR', 'DEX', 'CON', 'INT', 'WIS', 'CHA', 'PER', 'LCK', 'WIL', 'FOR', 'AGI', 'PRS']

export interface AbilityInvocation {
  abilityName: string
  argsText: string
  ability: GameState['player']['abilities'][number] | undefined
}

export interface MovementPlan {
  name: string
  desc: string
  xOff: number
  yOff: number
  items: string[]
  npcs: GameState['currentRoom']['npcs']
}

export interface ActionSkillContext {
  name: string
  level: number
  bonus: number
}

type StarterAtlasNode = {
  key: string
  name: string
  desc: string
  flags?: Record<string, string | number | boolean | null>
}

type StarterAtlasEdge = {
  x1: number
  y1: number
  x2: number
  y2: number
}

export interface StarterAtlasScaffold {
  nodes: Record<string, GameState['atlas']['nodes'][string]>
  edges: StarterAtlasEdge[]
}

export interface ResolvedMechanics {
  dc: number
  primaryStat: AttributeKey
  bonus: number
  chance: number
  bonusSummary: string
  bonusDetail: string
}

export function hasEntropyTrait(traits: Trait[]): boolean {
  return traits.some(t =>
    ENTROPY_KEYWORDS.some(kw =>
      t.name.toLowerCase().includes(kw) || t.desc.toLowerCase().includes(kw)
    )
  )
}

export function asAttributeKey(value: string): AttributeKey | null {
  const match = ATTRIBUTE_KEYS.find(key => key === value)
  return match ?? null
}

export function parseAbilityInvocation(gs: GameState, input: string): AbilityInvocation | null {
  if (!/^use ability:/i.test(input)) return null

  const raw = input.replace(/^use ability:/i, '').trim()
  if (!raw) return { abilityName: '', argsText: '', ability: undefined }

  const byName = (name: string) => gs.player.abilities.find(a => a.name.toLowerCase() === name.toLowerCase())
  const pipeIdx = raw.indexOf('|')
  if (pipeIdx >= 0) {
    const abilityName = raw.slice(0, pipeIdx).trim()
    const argsText = raw.slice(pipeIdx + 1).trim()
    return { abilityName, argsText, ability: byName(abilityName) }
  }

  const exact = byName(raw)
  if (exact) return { abilityName: exact.name, argsText: '', ability: exact }

  const byLength = [...gs.player.abilities].sort((a, b) => b.name.length - a.name.length)
  const prefixed = byLength.find(a => raw.toLowerCase().startsWith(`${a.name.toLowerCase()} `))
  if (prefixed) {
    return {
      abilityName: prefixed.name,
      argsText: raw.slice(prefixed.name.length).trim(),
      ability: prefixed,
    }
  }

  return { abilityName: raw, argsText: '', ability: undefined }
}

function shouldInferTargetArg(gs: GameState, ability: Ability): boolean {
  if ((gs.currentRoom.npcs?.length ?? 0) <= 0) return false
  const text = `${ability.name} ${ability.desc}`.toLowerCase()
  if (/\b(heal|mend|restore|shield|ward|protect|cleanse|buff|fortify|recover)\b/.test(text)) return false
  return /\b(attack|blast|bolt|strike|shot|smite|curse|hex|burn|arcane|pierce|slash|stab|hit|shock|storm|ray|missile)\b/.test(text)
}

export function getResolvedAbilityArgs(gs: GameState, ability: Ability): NonNullable<Ability['args']> {
  if (ability.args?.length) return ability.args
  if (shouldInferTargetArg(gs, ability)) {
    return [{ name: 'target', type: 'target', prompt: 'Choose target' }]
  }
  return []
}

function sanitizeActionText(text: string): string {
  return text
    .toLowerCase()
    .replace(/^use ability:\s*/i, '')
    .replace(/[^a-z0-9\s]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
}

function tokenizeSkillName(skill: Skill): string[] {
  const base = `${skill.id} ${skill.name}`
    .toLowerCase()
    .replace(/[_-]/g, ' ')
    .replace(/[^a-z0-9\s]/g, ' ')
  return base.split(/\s+/).filter(token => token.length > 2)
}

function isCombatSkill(skill: Skill): boolean {
  const text = `${skill.id} ${skill.name}`.toLowerCase()
  return /\b(sword|blade|combat|fighting|melee|archery|marksmanship|martial|brawling|duel)\b/.test(text)
}

function isMovementSkill(skill: Skill): boolean {
  const text = `${skill.id} ${skill.name}`.toLowerCase()
  return /\b(swim|swimming|climb|climbing|athletic|parkour|sprint|running|navigation|survival|ride|riding)\b/.test(text)
}

function scoreSkillMatch(skill: Skill, actionText: string, category: ActionCategory | undefined): number {
  if (!actionText) return 0
  const loweredSkill = skill.name.toLowerCase()
  const normalizedSkill = loweredSkill.replace(/[_-]/g, ' ')
  let score = 0

  if (actionText.includes(normalizedSkill)) score += 5
  for (const token of tokenizeSkillName(skill)) {
    if (actionText.includes(token)) score += 2
  }

  if (category === ActionCategory.attack && isCombatSkill(skill)) score += 2
  if (category === ActionCategory.move && isMovementSkill(skill)) score += 2
  if (category === ActionCategory.ability && actionText.includes('cast') && /\b(arcana|magic|spell|focus|ritual)\b/.test(loweredSkill)) {
    score += 2
  }

  return score
}

export function getActionSkillContext(gs: GameState, input: string, category: ActionCategory | undefined): ActionSkillContext | undefined {
  const actionText = sanitizeActionText(input)
  if (!actionText || !gs.player.skills?.length) return undefined

  const ranked = gs.player.skills
    .map(skill => ({
      skill,
      score: scoreSkillMatch(skill, actionText, category),
    }))
    .filter(entry => entry.score > 0)
    .sort((a, b) => b.score - a.score || b.skill.level - a.skill.level)

  const best = ranked[0]?.skill
  if (!best) return undefined

  const logShift = Math.log2(skillLevelMultiplier(best.level))
  const bonus = Math.max(-3, Math.min(8, Math.round(logShift * 2)))

  return {
    name: best.name,
    level: best.level,
    bonus,
  }
}

export function estimateSuccessChance(dc: number, bonus: number): number {
  const threshold = dc - bonus
  const successFaces = Math.max(1, Math.min(19, 21 - threshold)) // cap at 1 and 19 for nat 1/20 mechanics
  return Math.round((successFaces / 20) * 100)
}

export function resolveMechanicsBonus(
  gs: GameState,
  mechanics: { dc: number; primaryStat: string; bonus: number; chance?: number },
  skillBonus = 0,
  options: { category?: ActionCategory; input?: string } = {},
): ResolvedMechanics {
  const primaryStat = asAttributeKey(String(mechanics?.primaryStat ?? 'STR')) ?? 'STR'
  const dc = Number.isFinite(mechanics?.dc) ? Math.max(5, Math.min(30, Math.round(mechanics.dc))) : 12
  const attrVal = Number(gs.player.attributes?.[primaryStat]?.val ?? 10)
  const statBonus = Math.floor((attrVal - 10) / 2)

  const rawModifiers = gs.player.modifiers?.[primaryStat] ?? []
  const modifierBonus = rawModifiers.reduce((sum, mod) => (
    Number.isFinite(mod?.val) ? sum + Number(mod.val) : sum
  ), 0)

  // AI-provided bonus is treated as contextual adjustment over stat+modifier baseline.
  const aiRawBonus = Number.isFinite(mechanics?.bonus)
    ? Math.round(Number(mechanics.bonus))
    : (statBonus + modifierBonus)
  const situationalBonus = Math.max(-4, Math.min(4, aiRawBonus - (statBonus + modifierBonus)))

  const totalBonus = Math.max(-8, Math.min(20, statBonus + modifierBonus + skillBonus + situationalBonus))
  const chance = estimateSuccessChance(dc, totalBonus)
  const actionText = String(options.input ?? '').toLowerCase()
  const isOffensive = options.category === ActionCategory.attack
    || (options.category === ActionCategory.ability
      && /\b(attack|blast|bolt|strike|smite|curse|hex|burn|shock|ray|missile|slash|stab|hit)\b/.test(actionText))
  const impactPercent = isOffensive ? Math.max(40, Math.min(220, 100 + (totalBonus * 4))) : 100
  const impactConst = isOffensive ? Math.max(-8, Math.min(15, Math.round(totalBonus * 0.6))) : 0

  const bonusSummary = [
    `CHECK ${chance}% (${totalBonus >= 0 ? '+' : ''}${totalBonus})`,
    `IMPACT ${impactPercent}% (${impactConst >= 0 ? '+' : ''}${impactConst})`,
  ].join(' | ')
  const bonusDetail = [
    `Success/Failure profile: ${chance}% chance, constant ${totalBonus >= 0 ? '+' : ''}${totalBonus}`,
    `Effect/Damage profile: ${impactPercent}% modifier, constant ${impactConst >= 0 ? '+' : ''}${impactConst}`,
    '',
    `Primary stat (${primaryStat}): ${statBonus >= 0 ? '+' : ''}${statBonus}`,
    `Active modifiers (${primaryStat}): ${modifierBonus >= 0 ? '+' : ''}${modifierBonus}`,
    `Skill influence: ${skillBonus >= 0 ? '+' : ''}${skillBonus}`,
    `Context adjustment: ${situationalBonus >= 0 ? '+' : ''}${situationalBonus}`,
    `Total check bonus: ${totalBonus >= 0 ? '+' : ''}${totalBonus}`,
    isOffensive
      ? `Impact model: offensive action uses total bonus for damage/effect scaling.`
      : `Impact model: non-offensive action keeps neutral impact scaling.`,
  ].join('\n')

  return {
    dc,
    primaryStat,
    bonus: totalBonus,
    chance,
    bonusSummary,
    bonusDetail,
  }
}

export function inferMovementFallback(state: GameState, input: string): MovementPlan | null {
  const text = input.trim().toLowerCase()
  if (!text) return null

  const dirMap: Array<{ re: RegExp; xOff: number; yOff: number }> = [
    { re: /\b(north|up)\b/, xOff: 0, yOff: 1 },
    { re: /\b(south|down)\b/, xOff: 0, yOff: -1 },
    { re: /\b(east|right)\b/, xOff: 1, yOff: 0 },
    { re: /\b(west|left)\b/, xOff: -1, yOff: 0 },
  ]
  const hasMoveVerb = /\b(go|move|walk|head|travel|enter|return|leave|run|sprint|crawl|sneak|dash)\b/.test(text)

  for (const d of dirMap) {
    if (!d.re.test(text) || !hasMoveVerb) continue
    const nx = state.player.location.x + d.xOff
    const ny = state.player.location.y + d.yOff
    const node = state.atlas.nodes[`${nx}_${ny}`]
    return {
      name: node?.name ?? 'Uncharted Path',
      desc: node?.desc ?? `A newly reached path branching from ${state.currentRoom.name}.`,
      xOff: d.xOff,
      yOff: d.yOff,
      items: node?.items ?? [],
      npcs: [],
    }
  }

  const toMatch = text
    .replace(/\b(go|move|walk|head|travel|enter|return|leave|run|sprint|crawl|sneak|dash)\b/g, '')
    .replace(/\b(to|toward|towards|into|the|a|an)\b/g, ' ')
    .replace(/[^a-z0-9\s]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
  if (!toMatch || !hasMoveVerb) return null

  const entries = Object.entries(state.atlas.nodes)
  const exact = entries.find(([, node]) => node.name?.toLowerCase() === toMatch)
  const fuzzy = exact ?? entries.find(([, node]) => node.name?.toLowerCase().includes(toMatch))
  if (!fuzzy) return null

  const [targetKey, targetNode] = fuzzy
  const parsed = parseLocKey(targetKey)
  if (!parsed) return null

  const [tx, ty] = parsed
  const dx = tx - state.player.location.x
  const dy = ty - state.player.location.y
  if (dx === 0 && dy === 0) return null

  const xOff = Math.abs(dx) >= Math.abs(dy) ? Math.sign(dx) : 0
  const yOff = xOff !== 0 ? 0 : Math.sign(dy)
  if (xOff === 0 && yOff === 0) return null

  const stepKey = `${state.player.location.x + xOff}_${state.player.location.y + yOff}`
  const stepNode = state.atlas.nodes[stepKey] ?? targetNode
  return {
    name: stepNode.name ?? targetNode.name ?? 'Unknown Path',
    desc: stepNode.desc ?? targetNode.desc ?? `A route leading toward ${targetNode.name}.`,
    xOff,
    yOff,
    items: stepNode.items ?? [],
    npcs: [],
  }
}

function starterSeedTokens(seed: string): string[] {
  return String(seed || '')
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, ' ')
    .split(/\s+/)
    .filter(token => token.length >= 4)
    .slice(0, 12)
}

function flavorForDirection(seed: string, direction: string, step: number): string {
  const tokens = starterSeedTokens(seed)
  const fallback = ['wind', 'echo', 'stone', 'lantern', 'glass', 'signal', 'ember', 'salt']
  const index = Math.abs((direction.charCodeAt(0) * 7) + step * 11 + seed.length) % Math.max(1, tokens.length || fallback.length)
  return (tokens[index] || fallback[index % fallback.length]).replace(/^./, letter => letter.toUpperCase())
}

function directionSpec(direction: 'north' | 'east' | 'south' | 'west') {
  const specs = {
    north: { x: 0, y: 1, noun: 'Approach', tone: 'A route rising', farTone: 'A farther northern reach opens' },
    east: { x: 1, y: 0, noun: 'Span', tone: 'A route opening east', farTone: 'A farther eastern reach opens' },
    south: { x: 0, y: -1, noun: 'Passage', tone: 'A route dipping south', farTone: 'A farther southern reach opens' },
    west: { x: -1, y: 0, noun: 'Way', tone: 'A route bending west', farTone: 'A farther western reach opens' },
  }
  return specs[direction]
}

function starterNode(
  key: string,
  name: string,
  desc: string,
  flags?: Record<string, string | number | boolean | null>,
): StarterAtlasNode {
  return { key, name, desc, flags }
}

export function buildStarterAtlasScaffold(seed: string, roomName: string, roomDesc: string): StarterAtlasScaffold {
  const safeRoomName = String(roomName || '').trim() || 'Starting Point'
  const safeRoomDesc = String(roomDesc || '').trim()
  const nodes: StarterAtlasNode[] = [
    starterNode('0_0', safeRoomName, safeRoomDesc),
  ]
  const edges: StarterAtlasEdge[] = []

  ;(['north', 'east', 'south', 'west'] as const).forEach((direction) => {
    const spec = directionSpec(direction)
    const nearFlavor = flavorForDirection(seed, direction, 1)
    const farFlavor = flavorForDirection(seed, direction, 2)
    const nearKey = `${spec.x}_${spec.y}`
    const farKey = `${spec.x * 2}_${spec.y * 2}`
    nodes.push(starterNode(
      nearKey,
      `${nearFlavor} ${spec.noun}`,
      `${spec.tone} from ${safeRoomName}. ${nearFlavor} details suggest the world keeps extending beyond the immediate start.`,
      { presentation: 'passage' },
    ))
    nodes.push(starterNode(
      farKey,
      `${farFlavor} Outer ${spec.noun}`,
      `${spec.farTone} beyond ${safeRoomName}. The way is not fully known yet, but it does not feel like a dead end.`,
      { presentation: 'passage' },
    ))
    edges.push({ x1: 0, y1: 0, x2: spec.x, y2: spec.y })
    edges.push({ x1: spec.x, y1: spec.y, x2: spec.x * 2, y2: spec.y * 2 })
  })

  return {
    nodes: Object.fromEntries(nodes.map((node) => [node.key, {
      name: node.name,
      desc: node.desc,
      items: [],
      flags: node.flags ? { ...node.flags } : {},
      dormantFlags: [],
      clocks: [],
    }])),
    edges,
  }
}
